import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Button,
    Checkbox,
    FormControlLabel,
    makeStyles,
    Popover,
    PopoverOrigin,
    Theme,
    Typography,
} from '@material-ui/core'
import clsx from 'clsx'
import React from 'react'
import { useParams } from 'react-router-dom'
import { ReportTypes } from '../reportTypes'

const useStyles = makeStyles((theme: Theme) => ({
    title: {
        color: '#1899D4',
        fontSize: pxToRem(20),
        fontWeight: 500,
        marginLeft: theme.spacing(2),
    },
    menuItemTitle: {
        color: '#1899D4',
        fontSize: pxToRem(16),
        fontWeight: 500,
        marginLeft: theme.spacing(2),
        minWidth: pxToRem(270),
    },
    fullHorizontalLine: {
        marginLeft: pxToRem(-12),
        marginRight: pxToRem(-12),
    },
    separator: {
        margin: theme.spacing(2),
    },
    smallLabel: {
        position: 'absolute',
        right: 0,
        fontSize: theme.spacing(1),
        color: theme.palette.grey[700],
    },
    checkBoxGridItem: {
        margin: theme.spacing(2),
    },
    checkBox: {
        borderRadius: 0,
        color: theme.palette.common.black,
        border: pxToRem(1) + ' solid #C9C9C9',
        height: pxToRem(15),
        width: pxToRem(15),
        marginRight: theme.spacing(1),
        '&$checked': {
            color: theme.palette.common.black,
        },
        '& span': {
            '& svg.MuiSvgIcon-root': {
                color: 'transparent',
            },
        },
        '&.Mui-focusVisible': {
            outline: '3px solid black',
            borderRadius: '3px',
        },
    },

    checkBoxChecked: {
        border: pxToRem(1) + ' solid transparent',
        '& span': {
            '& svg.MuiSvgIcon-root': {
                color: '#1899D4',
            },
        },
    },

    formLabelRadioContainer: {
        marginLeft: theme.spacing(0),
        '& span.MuiTypography-body1': {
            fontSize: pxToRem(14),
            color: '#424242',
            fontWeight: 'normal',
        },
    },
    actionBtn: {
        borderRadius: 100,
        fontSize: pxToRem(14),
        fontWeight: 500,
        backgroundColor: theme.palette.common.white,
        border: '1px solid #1899D4',
        color: '#1899D4',
        '&:hover': {
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
            color: '#1899D4',
            backgroundColor: theme.palette.common.white,
        },
    },
    closeBtn: {
        color: theme.palette.common.white,
        backgroundColor: '#1899D4',
        '&:hover': {
            color: theme.palette.common.white,
            backgroundColor: '#1899D4',
        },
    },
    btnSpan: {
        fontSize: pxToRem(14),
    },
    checkboxLabel: {
        marginBottom: 0,
        '& p': {
            fontSize: pxToRem(14),
        },
    },
    popOverContentContainer: {
        padding: '12px',
    },
}))

export interface CompleteMenuItemConfig extends MenuItemConfig {
    isDisabled?: boolean
    smallLabel?: string
}

export interface ReportSelectPopoverProps {
    anchorElement: Element | null
    isActionPopoverDisplayed: boolean
    handleActionPopoverClick: (selectedItems: string[] | undefined) => void
    anchorOrigin?: PopoverOrigin
    transformOrigin?: PopoverOrigin
    clearTrigger: boolean
    resetClearTrigger: () => void
}

export const ReportSelectPopover: React.FC<ReportSelectPopoverProps> = ({
    anchorElement,
    isActionPopoverDisplayed,
    handleActionPopoverClick,
    anchorOrigin,
    transformOrigin,
    clearTrigger,
    resetClearTrigger,
}) => {
    const { projectId } = useParams<{ projectId?: string }>()
    const [selectedItems, setSelectedItems] = React.useState<string[]>(() => {
        // Load persisted selected items for the current project from localStorage
        const savedReportTypes = localStorage.getItem(
            `selectedReportTypes-${projectId}`
        )
        if (savedReportTypes) {
            try {
                return JSON.parse(savedReportTypes)
            } catch (error) {
                console.error(
                    'Error parsing savedReportTypes from localStorage:',
                    error
                )
                return []
            }
        }
        return []
    })

    // Save selected items to localStorage for the current project whenever they change
    React.useEffect(() => {
        if (selectedItems.length > 0) {
            localStorage.setItem(
                `selectedReportTypes-${projectId}`,
                JSON.stringify(selectedItems)
            )
        } else {
            // Clear saved reports for the current project if no items are selected
            localStorage.removeItem(`selectedReportTypes-${projectId}`)
        }
    }, [selectedItems, projectId])

    React.useEffect(() => {
        if (clearTrigger) {
            setSelectedItems([])
            resetClearTrigger()
        }
    }, [clearTrigger, resetClearTrigger])

    const reportTypeMenuItems1: CompleteMenuItemConfig[] = React.useMemo(
        () => [
            {
                label: ReportTypes.Submittals,
                isDisabled: false,
            },
            {
                label: ReportTypes.UnresolvedOptions,
                isDisabled: false,
            },
        ],
        []
    )
    const reportTypeMenuItems2: CompleteMenuItemConfig[] = React.useMemo(
        () => [],
        []
    )

    const classes = useStyles({})

    const handleCheckboxChange = (label: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(label)) {
                // Remove the label if it's already selected
                return prevSelectedItems.filter((item) => item !== label)
            } else {
                // Add the label if it's not selected
                return [...prevSelectedItems, label]
            }
        })
    }

    return (
        <Popover
            className="popOverMenu"
            data-testid="report-select-popover"
            open={isActionPopoverDisplayed}
            anchorEl={anchorElement}
            onClose={() => handleActionPopoverClick(selectedItems)}
            anchorOrigin={anchorOrigin}
            transformOrigin={
                transformOrigin
                    ? transformOrigin
                    : {
                          vertical: 'top',
                          horizontal: 'right',
                      }
            }
            aria-labelledby="report-select-title"
        >
            <div className={classes.popOverContentContainer}>
                <GridItem>
                    <Typography
                        id="report-select-title"
                        className={classes.title}
                        component="div"
                    >
                        Report Type
                    </Typography>
                </GridItem>

                <hr className={classes.fullHorizontalLine} />
                {reportTypeMenuItems1.map(
                    (menuItem: CompleteMenuItemConfig) => {
                        const isSelected = selectedItems.includes(
                            menuItem.label ?? ''
                        )

                        return (
                            <GridItem
                                key={menuItem.label}
                                className={classes.checkBoxGridItem}
                            >
                                <FormControlLabel
                                    className={classes.formLabelRadioContainer}
                                    control={
                                        <Checkbox
                                            id={`checkbox-${menuItem.label}`}
                                            size="small"
                                            data-testid={`checkbox-${menuItem.label}`}
                                            disableRipple
                                            classes={{
                                                root: classes.checkBox,
                                                checked:
                                                    classes.checkBoxChecked,
                                            }}
                                            color="default"
                                            onChange={() =>
                                                handleCheckboxChange(
                                                    menuItem.label ?? ''
                                                )
                                            }
                                            checked={isSelected}
                                            inputProps={{
                                                role: 'checkbox',
                                                'aria-checked': isSelected,
                                                'aria-labelledby': `label-${menuItem.label}`,
                                                tabIndex: 0,
                                            }}
                                        />
                                    }
                                    label={
                                        <label
                                            id={`label-${menuItem.label}`}
                                            htmlFor={`checkbox-${menuItem.label}`}
                                            className={classes.checkboxLabel}
                                        >
                                            <Typography>
                                                {menuItem.label}
                                            </Typography>
                                        </label>
                                    }
                                />
                            </GridItem>
                        )
                    }
                )}
                {false && (
                    <>
                        <hr className={classes.separator} />
                        <GridItem>
                            <Typography
                                className={classes.menuItemTitle}
                                component="div"
                            >
                                Subheading-B Reports
                            </Typography>
                        </GridItem>
                        {reportTypeMenuItems2.map(
                            (menuItem: CompleteMenuItemConfig, index) => {
                                return (
                                    <GridItem
                                        className={classes.checkBoxGridItem}
                                    >
                                        <FormControlLabel
                                            className={
                                                classes.formLabelRadioContainer
                                            }
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    data-testid="notesToSpecifier"
                                                    disableRipple
                                                    classes={{
                                                        root: classes.checkBox,
                                                        checked:
                                                            classes.checkBoxChecked,
                                                    }}
                                                    color="default"
                                                    onClick={() => {}}
                                                />
                                            }
                                            label={menuItem.label}
                                        />
                                    </GridItem>
                                )
                            }
                        )}
                    </>
                )}

                <hr className={classes.fullHorizontalLine} />
                <GridContainer
                    direction="row"
                    spacing={2}
                    justifyContent="flex-end"
                >
                    <GridItem>
                        <GridContainer direction="column">
                            <Button
                                variant="contained"
                                disableElevation
                                color="secondary"
                                onClick={() => {
                                    handleActionPopoverClick([])
                                }}
                                className={classes.actionBtn}
                                aria-label="Clear selected report types"
                                role="button"
                                tabIndex={0}
                            >
                                <Typography
                                    component="span"
                                    className={classes.btnSpan}
                                >
                                    Clear
                                </Typography>
                            </Button>
                        </GridContainer>
                    </GridItem>
                    <GridItem>
                        <GridContainer direction="column">
                            <Button
                                variant="contained"
                                disableElevation
                                color="secondary"
                                onClick={() =>
                                    handleActionPopoverClick(selectedItems)
                                }
                                className={clsx(
                                    classes.actionBtn,
                                    classes.closeBtn
                                )}
                                aria-label="Close popover"
                                role="button"
                                tabIndex={0}
                            >
                                <Typography
                                    component="span"
                                    className={classes.btnSpan}
                                >
                                    Close
                                </Typography>
                            </Button>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </div>
        </Popover>
    )
}
